import React, { useMemo } from 'react';
import { inicialesDe, colorDe } from '../AsignadoBubble';
import { cargaPorPersona, cargaPorEquipo } from '../../utils/ordenesTrabajo';

/**
 * Who is carrying what: open OTs per person and per equipo. Clicking a row
 * filters the tablero.
 *
 * @param {Object} props
 * @param {Array<Object>} props.abiertas
 * @param {{equipos:Array, fases:Array}} props.config
 * @param {Array<Object>} props.miembros
 * @param {Function} props.onPersona  - (uid)
 * @param {Function} props.onEquipo  - (equipoId)
 */
export function OtCarga({ abiertas, config, miembros, onPersona, onEquipo }) {
  const personas = useMemo(() => cargaPorPersona(abiertas, miembros), [abiertas, miembros]);
  const equipos = useMemo(() => cargaPorEquipo(abiertas, config), [abiertas, config]);
  const porId = useMemo(() => {
    const m = {};
    miembros.forEach((x) => { m[x.id] = x; });
    return m;
  }, [miembros]);
  const max = Math.max(1, ...personas.map((p) => p.total));

  return (
    // Stacked, not side by side: the persona table grows a column per tipo
    // de orden, and in a half-width card the last ones were clipped.
    <div className="space-y-4">
      <div className="bg-white rounded-lg border border-zinc-200 shadow-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-azul-surelia text-left text-xs font-medium text-white">
              <th className="px-3 py-2 w-full">Persona</th>
              <th className="px-3 py-2 text-right whitespace-nowrap">Abiertas</th>
              {config.flujos.map((f) => (
                <th key={f.id} className="px-3 py-2 text-right whitespace-nowrap">{f.nombre}</th>
              ))}
              <th className="px-3 py-2 text-right whitespace-nowrap">Más vieja</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100">
            {personas.map((p) => {
              const m = porId[p.uid];
              return (
                <tr key={p.uid} className="hover:bg-zinc-50 cursor-pointer" onClick={() => onPersona?.(p.uid)}>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-2">
                      <span className={`flex items-center justify-center rounded-full w-6 h-6 text-[10px] font-semibold ${colorDe(p.uid)}`}>
                        {inicialesDe(m || { nombre: p.uid })}
                      </span>
                      <span className="text-zinc-800">{m?.nombre || m?.correo || p.uid}</span>
                    </div>
                    <div className="mt-1 h-1 rounded bg-zinc-100">
                      <div className="h-1 rounded bg-azul-surelia" style={{ width: `${(p.total / max) * 100}%` }} />
                    </div>
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums font-medium text-zinc-800 whitespace-nowrap">{p.total}</td>
                  {config.flujos.map((f) => (
                    <td key={f.id} className="px-3 py-2 text-right tabular-nums text-zinc-600 whitespace-nowrap">{p.porTipo[f.id] || 0}</td>
                  ))}
                  <td className="px-3 py-2 text-right tabular-nums text-zinc-600 whitespace-nowrap">{p.masVieja === null ? '—' : `${p.masVieja} d`}</td>
                </tr>
              );
            })}
            {personas.length === 0 && (
              <tr><td colSpan={3 + config.flujos.length} className="px-3 py-6 text-center text-zinc-400">Sin miembros</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="bg-white rounded-lg border border-zinc-200 shadow-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-azul-surelia text-left text-xs font-medium text-white">
              <th className="px-3 py-2 w-full">Equipo</th>
              <th className="px-3 py-2 text-right whitespace-nowrap">Asignadas</th>
              <th className="px-3 py-2 text-right whitespace-nowrap">En su fase</th>
              <th className="px-3 py-2 text-right whitespace-nowrap">Total</th>
              <th className="px-3 py-2 text-right whitespace-nowrap">Miembros</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100">
            {equipos.map((e) => (
              <tr key={e.equipo.id} className="hover:bg-zinc-50 cursor-pointer" onClick={() => onEquipo?.(e.equipo.id)}>
                <td className="px-3 py-2 text-zinc-800">{e.equipo.nombre}</td>
                <td className="px-3 py-2 text-right tabular-nums text-zinc-600 whitespace-nowrap">{e.asignadas}</td>
                <td className="px-3 py-2 text-right tabular-nums text-zinc-600 whitespace-nowrap">{e.enSuFase}</td>
                <td className="px-3 py-2 text-right tabular-nums font-medium text-zinc-800 whitespace-nowrap">{e.total}</td>
                <td className="px-3 py-2 text-right tabular-nums text-zinc-600 whitespace-nowrap">{(e.equipo.miembros || []).length}</td>
              </tr>
            ))}
            {equipos.length === 0 && (
              <tr><td colSpan={5} className="px-3 py-6 text-center text-zinc-400">Sin equipos configurados</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default OtCarga;
